import { format, parseISO } from "date-fns";
import { useState } from "react";
import { issuingBodysList } from "../../../shared/constants/issuing-body-conts";
import { MailingData } from "../../../shared/models/backend/mailing/mailing";
import { DateUtils } from "../../../shared/utils/date-utils";
import { ValidationUtils } from "../../../utils/validation-utils";

type ClientDataFormProps = {
  phone: string;
  email: string;
  cardAcquired?: "yes" | "no" | any;
  cardExpirationDate?: number;
  cardType?: "nacional" | "internacional";
  insuranceProtection?: "sim" | "nao";
  specialCheck?: "sim" | "nao";
  magneticCard?: "sim" | "nao";
  magneticCardType?: "definitivo" | "nb";
  currentAccount?: "sim" | "nao";
  currentAccountIndividual?: "sim" | "nao";
} & Pick<
  MailingData,
  | "bairro"
  | "cep"
  | "cidade"
  | "ufEndereco"
  | "numero"
  | "complemento"
  | "logradouro"
  | "nomeMae"
  | "escolaridade"
  | "nomePai"
  | "dataEmissao"
  | "ufEmissor"
  | "slgOrgemissor"
  | "numeroDocumento"
>;

type ClientDataFormErrorsProps = Partial<
  Record<keyof ClientDataFormProps, string>
>;

const mandatoryFields = [
  "phone",
  "email",
  "logradouro",
  "numero",
  "bairro",
  "cidade",
  "cep",
  "ufEndereco",
  "numeroDocumento",
  "ufEmissor",
  "slgOrgemissor",
  "dataEmissao",
  "cardAcquired",
];

export const useClientDataForm = () => {
  const [form, setForm] = useState<ClientDataFormProps | undefined>();
  const [formErrors, setFormErrors] = useState<ClientDataFormErrorsProps>({});

  const getFormInitialValues = (
    mailingData: MailingData,
    mailingUserData: any
  ): ClientDataFormProps => {
    console.log('DEBUG - getFormInitialValues called with:', { mailingData, mailingUserData });
    
    // Tentar recuperar dados do localStorage primeiro
    let localStorageUserData = null;
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        localStorageUserData = JSON.parse(userDataStr);
        console.log('DEBUG - Dados recuperados do localStorage:', localStorageUserData);
      }
    } catch (error) {
      console.log('DEBUG - Erro ao recuperar dados do localStorage:', error);
    }
    const {
      bairro,
      cep,
      cidade,
      ufEndereco,
      numero,
      complemento,
      logradouro,
      nomeMae,
      escolaridade,
    } = mailingData;

    const getSlgOrgemissorDefaultValue = () => {
      if (!mailingData?.slgOrgemissor) return "";
      else if (
        issuingBodysList?.find(
          (item) => item.value === mailingData?.slgOrgemissor
        )
      ) {
        return mailingData?.slgOrgemissor;
      }

      return "OUTROS";
    };

    // Priorizar dados do localStorage para campos adicionais, depois dados do servidor
    const finalUserData = localStorageUserData || mailingUserData;
    
    const result = {
      phone: (
        finalUserData?.phone ||
        mailingUserData?.phone ||
        mailingData.telefones[0] ||
        ""
      ).replaceAll("+55", ""),
      email: finalUserData?.email || mailingUserData?.email || mailingData.emails[0],
      bairro: finalUserData?.bairro || mailingUserData?.bairro || bairro || "",
      cep: finalUserData?.cep || mailingUserData?.cep || cep || "",
      cidade: finalUserData?.cidade || mailingUserData?.cidade || cidade || "",
      ufEndereco: finalUserData?.ufEndereco || mailingUserData?.ufEndereco || ufEndereco || "",
      numero: finalUserData?.numero || mailingUserData?.numero || numero || "",
      complemento: finalUserData?.complemento || mailingUserData?.complemento || complemento || "",
      logradouro: finalUserData?.logradouro || mailingUserData?.logradouro || logradouro || "",
      nomeMae: finalUserData?.nomeMae || mailingUserData?.nomeMae || nomeMae || "",
      escolaridade: finalUserData?.escolaridade || mailingUserData?.escolaridade || escolaridade || "",
      nomePai: finalUserData?.nomePai || mailingUserData?.nomePai || "",
      dataEmissao: finalUserData?.dataEmissao
        ? (typeof finalUserData.dataEmissao === 'string' && finalUserData.dataEmissao.includes('/') 
           ? finalUserData.dataEmissao 
           : format(parseISO(finalUserData.dataEmissao), "dd/MM/yyyy"))
        : mailingUserData?.dataEmissao
        ? format(parseISO(mailingUserData?.dataEmissao), "dd/MM/yyyy")
        : "",
      ufEmissor: finalUserData?.ufEmissor || mailingUserData?.ufEmissor || "",
      slgOrgemissor: finalUserData?.slgOrgemissor || getSlgOrgemissorDefaultValue(),
      numeroDocumento:
        finalUserData?.numeroDocumento || mailingUserData?.numeroDocumento || mailingData?.numeroDocumento || "",
      
      // Campos adicionais - priorizar localStorage
      cardAcquired: (() => {
        if (localStorageUserData?.cardAcquired !== undefined) {
          return typeof localStorageUserData.cardAcquired === "boolean"
            ? localStorageUserData.cardAcquired ? "yes" : "no"
            : localStorageUserData.cardAcquired;
        }
        return typeof mailingUserData?.cardAcquired === "boolean"
          ? mailingUserData?.cardAcquired ? "yes" : "no"
          : "";
      })(),
      cardExpirationDate: localStorageUserData?.cardExpirationDate || mailingUserData?.cardExpirationDate || 10,
      cardType: localStorageUserData?.cardType || mailingUserData?.cardType || "nacional",
      insuranceProtection: localStorageUserData?.insuranceProtection || mailingUserData?.insuranceProtection || "nao",
      
      specialCheck: (() => {
        if (localStorageUserData?.specialCheck !== undefined) {
          return typeof localStorageUserData.specialCheck === "boolean"
            ? localStorageUserData.specialCheck ? "sim" : "nao"
            : localStorageUserData.specialCheck;
        }
        return typeof mailingUserData?.specialCheck === "boolean"
          ? mailingUserData?.specialCheck ? "sim" : "nao"
          : mailingUserData?.specialCheck || "nao";
      })(),
      
      magneticCard: (() => {
        if (localStorageUserData?.magneticCard !== undefined) {
          return typeof localStorageUserData.magneticCard === "boolean"
            ? localStorageUserData.magneticCard ? "sim" : "nao"
            : localStorageUserData.magneticCard;
        }
        return typeof mailingUserData?.magneticCard === "boolean"
          ? mailingUserData?.magneticCard ? "sim" : "nao"
          : mailingUserData?.magneticCard || "nao";
      })(),
      magneticCardType: localStorageUserData?.magneticCardType || mailingUserData?.magneticCardType || "definitivo",
      
      currentAccount: (() => {
        if (localStorageUserData?.currentAccount !== undefined) {
          return typeof localStorageUserData.currentAccount === "boolean"
            ? localStorageUserData.currentAccount ? "sim" : "nao"
            : localStorageUserData.currentAccount;
        }
        return typeof mailingUserData?.currentAccount === "boolean"
          ? mailingUserData?.currentAccount ? "sim" : "nao"
          : mailingUserData?.currentAccount || "nao";
      })(),
      
      currentAccountIndividual: (() => {
        if (localStorageUserData?.currentAccountIndividual !== undefined) {
          return typeof localStorageUserData.currentAccountIndividual === "boolean"
            ? localStorageUserData.currentAccountIndividual ? "sim" : "nao"
            : localStorageUserData.currentAccountIndividual;
        }
        return typeof mailingUserData?.currentAccountIndividual === "boolean"
          ? mailingUserData?.currentAccountIndividual ? "sim" : "nao"
          : mailingUserData?.currentAccountIndividual || "nao";
      })(),
    };
    
    console.log('DEBUG - getFormInitialValues result:', result);
    return result;
  };

  const validateForm = (): boolean => {
    let isFormValid = true;
    mandatoryFields.forEach((field) => {
      const isFieldValid = form[field] !== undefined && form[field] !== "";

      if (!isFieldValid) {
        setFormErrors((current) => ({
          ...current,
          [field]: "Campo obrigatório.",
        }));
        isFormValid = false;
        return;
      }

      if (field === "cardAcquired") {
        if (form.cardAcquired === "yes" && !form.cardExpirationDate) {
          isFormValid = false;
        }
      }

      if (field === "dataEmissao") {
        const isDateValid = ValidationUtils.validateBrazilianStringDate(
          form[field]
        );

        if (!isDateValid) {
          setFormErrors((current) => ({
            ...current,
            [field]: "Campo data da emissão é inválido.",
          }));
          isFormValid = false;
        }
      }

      if (field === "phone") {
        const isPhoneValid = ValidationUtils.validatePhone(form[field]);
        if (!isPhoneValid) {
          setFormErrors((current) => ({
            ...current,
            [field]: "Campo telefone é inválido.",
          }));
          isFormValid = false;
          return;
        }
      } else if (field === "email") {
        const isEmailValid = ValidationUtils.validateEmail(form[field]);
        if (!isEmailValid) {
          setFormErrors((current) => ({
            ...current,
            [field]: "Campo email é inválido.",
          }));
          isFormValid = false;
          return;
        }
      }
    });

    return isFormValid;
  };

  // const isFormValid = useMemo(() => {
  //   if (!form) return false;
  //   return validateForm();
  // }, [form]);

  const getChangedData = (baseData: MailingData, userData: any) => {
    console.log('DEBUG - getChangedData called with:', { baseData, userData, form });
    const result = JSON.parse(JSON.stringify(form));

    // Converter cardAcquired para boolean
    result.cardAcquired = result.cardAcquired === "yes" ? true : false;

    // Se não tem cartão de crédito, remover campos relacionados
    if (!result.cardAcquired) {
      delete result.cardExpirationDate;
      delete result.cardType;
      delete result.insuranceProtection;
    }

    // Converter outros campos boolean
    if (result.specialCheck) {
      result.specialCheck = result.specialCheck === "sim" ? true : false;
    }
    
    if (result.magneticCard) {
      result.magneticCard = result.magneticCard === "sim" ? true : false;
      if (!result.magneticCard) {
        delete result.magneticCardType;
      }
    }
    
    if (result.currentAccount) {
      result.currentAccount = result.currentAccount === "sim" ? true : false;
      if (!result.currentAccount) {
        delete result.currentAccountIndividual;
      } else if (result.currentAccountIndividual) {
        result.currentAccountIndividual = result.currentAccountIndividual === "sim" ? true : false;
      }
    }

    // Formatar data
    if (result.dataEmissao) {
      result.dataEmissao = DateUtils.stringToFormattedDate(result.dataEmissao);
    }
    
    console.log('DEBUG - getChangedData result:', result);
    return result;

    // const result: any = {};

    // for (const key in form) {
    //   if (form[key] !== (userData?.[key] || baseData?.[key])) {
    //     result[key] = form[key];
    //   }
    // }

    // result.cardAcquired = result.cardAcquired === "yes" ? true : false;

    // if (!result.cardAcquired) {
    //   delete result.cardExpirationDate;
    // }

    // if (result.cardExpirationDate) result.cardExpirationDate = 10;

    // result.dataEmissao = new Date(result.dataEmissao);

    // return result;
  };

  return {
    form,
    setForm,
    formErrors,
    setFormErrors,
    getFormInitialValues,
    validateForm,
    getChangedData,
  };
};
